import {
  COLLECTION_CURRENCIES,
  DEFAULT_CURRENCY,
  getCurrencyLabel,
  STANDARD_TEMPLATE_CURRENCY,
} from "@repo/shared";
import type { LocalizedString, PortfolioCollectionListItem, RiskModel } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateCollection,
  useCreateCollectionFromTemplate,
  useUpdateCollection,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./portfolio-collection-dialog.i18n";

type TemplateKey = "standard" | "blank";

// The currency each template starts with; the designer can change it before creating.
const TEMPLATE_CURRENCY: Record<TemplateKey, string> = {
  standard: STANDARD_TEMPLATE_CURRENCY,
  blank: DEFAULT_CURRENCY,
};

type PortfolioCollectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  riskModelId?: string;
  riskModels: RiskModel[];
  collection?: PortfolioCollectionListItem | null;
};

export function PortfolioCollectionDialog({
  open,
  onOpenChange,
  riskModelId,
  riskModels,
  collection,
}: PortfolioCollectionDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const createCollection = useCreateCollection();
  const createFromTemplate = useCreateCollectionFromTemplate();
  const updateCollection = useUpdateCollection();

  const TEMPLATE_OPTIONS = [
    {
      key: "standard" as const,
      label: t.templateStandard,
      subtitle: t.templateStandardSubtitle,
      description: t.templateStandardDescription,
      defaultName: t.templateStandardDefaultName,
    },
    {
      key: "blank" as const,
      label: t.templateBlank,
      subtitle: t.templateBlankSubtitle,
      description: t.templateBlankDescription,
      defaultName: "",
    },
  ];

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>("standard");
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [description, setDescription] = useState("");
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const [selectedRiskModelId, setSelectedRiskModelId] = useState("");
  const [currency, setCurrency] = useState(TEMPLATE_CURRENCY.standard);

  const isPending =
    createCollection.isPending || createFromTemplate.isPending || updateCollection.isPending;
  const [error, setError] = useState<string | null>(null);

  // The picker shows only active risk models. If the collection already has an
  // inactive risk model assigned (deactivated after assignment), include it so
  // the disabled dropdown can still render the name.
  const displayedRiskModels: Array<{ id: string; nameI18n: LocalizedString }> =
    collection?.riskModel && !riskModels.some((m) => m.id === collection.riskModel?.id)
      ? [...riskModels, { id: collection.riskModel.id, nameI18n: collection.riskModel.nameI18n }]
      : riskModels;

  useEffect(() => {
    if (open) {
      if (collection) {
        // Editing existing collection
        const collectionName = localize(collection.nameI18n);
        const collectionDescription = collection.descriptionI18n
          ? localize(collection.descriptionI18n)
          : "";
        setName(collectionName);
        setNameI18n(collection.nameI18n || {});
        setDescription(collectionDescription);
        setDescriptionI18n(collection.descriptionI18n || {});
        setSelectedRiskModelId(collection.riskModelId || "");
        setSelectedTemplate("blank");
      } else {
        // Creating new collection — default to Standard template
        const defaultTemplate = TEMPLATE_OPTIONS.find((tmpl) => tmpl.key === "standard");
        setSelectedTemplate("standard");
        setName(defaultTemplate?.defaultName || "");
        setNameI18n({});
        setDescription(defaultTemplate?.description || "");
        setDescriptionI18n({});
        setSelectedRiskModelId(riskModelId || "");
        setCurrency(TEMPLATE_CURRENCY.standard);
      }
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, riskModelId, collection, localize]);

  const handleTemplateChange = (key: TemplateKey) => {
    setSelectedTemplate(key);
    if (!collection) {
      const template = TEMPLATE_OPTIONS.find((tmpl) => tmpl.key === key);
      setName(template?.defaultName || "");
      setDescription(template?.description || "");
      setCurrency(TEMPLATE_CURRENCY[key]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const finalNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name,
    };
    const finalDescriptionI18n: LocalizedString | undefined = description
      ? {
          ...descriptionI18n,
          [userLanguage]: description,
        }
      : undefined;

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => {
        setError(err.message || (collection ? t.failedToUpdate : t.failedToCreate));
      },
    };

    if (collection) {
      updateCollection.mutate(
        {
          id: collection.id,
          data: {
            nameI18n: finalNameI18n,
            descriptionI18n: finalDescriptionI18n,
            ...(collection.riskModelId ? {} : { riskModelId: selectedRiskModelId || undefined }),
          },
        },
        callbacks,
      );
    } else if (selectedTemplate === "standard") {
      createFromTemplate.mutate(
        {
          template: "standard",
          nameI18n: finalNameI18n,
          descriptionI18n: finalDescriptionI18n,
          riskModelId: selectedRiskModelId || undefined,
          currency,
        },
        callbacks,
      );
    } else {
      createCollection.mutate(
        {
          nameI18n: finalNameI18n,
          descriptionI18n: finalDescriptionI18n,
          riskModelId: selectedRiskModelId || undefined,
          currency,
        },
        callbacks,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{collection ? t.editTitle : t.createTitle}</DialogTitle>
          <DialogDescription>
            {collection ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {!collection && (
              <div className="space-y-3">
                <Label>{t.chooseTemplate}</Label>
                <div className="space-y-2">
                  {TEMPLATE_OPTIONS.map((template) => (
                    <button
                      key={template.key}
                      type="button"
                      onClick={() => handleTemplateChange(template.key)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border transition-colors",
                        selectedTemplate === template.key
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-muted-foreground/50 hover:bg-muted/50",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                              selectedTemplate === template.key
                                ? "border-primary"
                                : "border-muted-foreground/50",
                            )}
                          >
                            {selectedTemplate === template.key && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <span className="font-medium">{template.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{template.subtitle}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 ml-6">
                        {template.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <LocalizedInput
              id="name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={setName}
              onI18nChange={setNameI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
            />

            <LocalizedInput
              id="description"
              label={t.description}
              value={description}
              i18nValue={descriptionI18n}
              onChange={setDescription}
              onI18nChange={setDescriptionI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.descriptionPlaceholder}
              multiline
              rows={3}
            />

            <div className="space-y-2">
              <Label htmlFor="riskModelId">{t.riskModel}</Label>
              <Select
                value={selectedRiskModelId || "none"}
                onValueChange={(value) => setSelectedRiskModelId(value === "none" ? "" : value)}
                disabled={!!collection?.riskModelId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.riskModelPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">{t.noRiskModel}</span>
                  </SelectItem>
                  {displayedRiskModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {localize(model.nameI18n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {collection?.riskModelId
                  ? t.riskModelCannotChange
                  : collection
                    ? t.riskModelSetOnceHint
                    : t.riskModelHint}
              </p>
            </div>

            {!collection && (
              <div className="space-y-2">
                <Label htmlFor="currency">{t.currency}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {getCurrencyLabel(c.code, userLanguage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.currencyHint}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {collection ? t.save : t.create}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
